/**
 * Community partners: senior centers, libraries and similar organisations
 * that promote GranGuard to the people they serve, plus campaign partners
 * (a film, a brand) that point a national audience at it. A partner co-brands
 * the GranGuard face (granguard.scamdefense.org): a partnership strip under
 * the header, a personalised hero line, a "who to call" band with the
 * partner's own contacts and the local or national reporting lines, and a
 * footer block. The product stays GranGuard; the partner is the trusted
 * messenger.
 *
 * Resolution: `?partner=<slug>` (persisted for the tab, like `?site=`) or the
 * subdomain (`garvey.scamdefense.org`, `beekeeper.granguard.org`). The
 * GranGuard face is selected by the same host in lib/site.ts.
 *
 * First partner: the Garvey Senior Activity Center in Leonardtown, MD
 * (St. Mary's County Aging & Human Services), 2026-09-09.
 * Second: The Beekeeper 2 "Protect the Hive" campaign face, 2026-09-10, a
 * private preview for the studio's head of promotions.
 * Third: the Edward A. Myerberg Center, Baltimore MD, 2026-09-14, a gated
 * preview for its program director ahead of a sponsored-tenant offer.
 * Fourth: Lafayette Federal Credit Union, Rockville MD, 2026-09-15, a gated
 * demo of the member-benefit layer for their SVP BizDev and marketing lead.
 */
export type LocalHelpLine = {
  name: string
  /** Omit for an online-only service (FBI IC3). */
  phone?: string
  tel?: string
  /** Further numbers shown as labelled lines under the main one. */
  extraPhones?: { label: string; phone: string; tel: string }[]
  description: string
  url?: string
  /** Link text for `url`. Defaults to "Report online"; say what the page really is. */
  linkLabel?: string
}

export type CommunityPartner = {
  slug: string
  name: string
  shortName: string
  /** Parent organisation shown in the strip and footer. */
  organization: string
  /**
   * Lead-in for the nav lockup and mobile strip. Defaults to
   * "In partnership with"; a campaign partner may prefer "Presented with".
   */
  partnerLabel?: string
  /** Hero eyebrow; replaces the default "For you and your family" line. */
  audience: string
  /**
   * Hero headline. Defaults to the locked "Learn how to spot scams."; a
   * campaign face can carry its own line (Beekeeper: "Protect your hive.").
   */
  heroHeadline?: string
  /**
   * Accent used for the hero eyebrow, rule and footnote icon in place of the
   * GranGuard violet. Only set this when the partner's identity should tint
   * the hero; the CTAs stay violet so the product still reads as GranGuard.
   */
  heroAccent?: string
  /** Decorative overlay on the hero artwork. */
  heroPattern?: 'honeycomb'
  /** Optional small line under the hero CTAs; omit to show none. */
  heroFootnote?: string
  /** Heading of the "who to call" band. */
  localHelpHeading: string
  /** One sentence under the local-help heading. */
  localHelpIntro: string
  logoUrl: string
  logoAlt: string
  colors: { primary: string; deep: string; accent: string }
  /**
   * Venue details for a place people can walk into. Omit for a campaign
   * partner: the partner card in the band and the footer address line then
   * do not render.
   */
  address?: string[]
  phone?: string
  tel?: string
  hours?: string[]
  website?: string
  websiteLabel?: string
  /** Where to turn (locally or nationally) when something has gone wrong. */
  localHelp: LocalHelpLine[]
  /**
   * Financial-institution partners: the "Check with <partner>" box shown on
   * every Scam Checker result. Numbers must match the partner's own contact
   * page (the server never trusts client-sent numbers; this is display only).
   */
  verifyContact?: {
    heading: string
    body: string
    phones: { label: string; phone: string; tel: string }[]
    website: string
    websiteLabel: string
    /** Chapter 6 ("If money moved"): who to call first, shown under the slide body. */
    firstCall: { lead: string; phone: string; tel: string; after: string }
  }
  /** "bank" keeps the source copy; "creditUnion" applies the CU wording swap. */
  terminology: 'bank' | 'creditUnion'
  /**
   * When set, PartnerGate covers the whole site with a frosted lock screen
   * until this code is entered (or passed as ?code=). A courtesy screen so a
   * partner's branding isn't publicly browsable before they've agreed to it,
   * not security: the code and content still ship in the client bundle.
   */
  accessCode?: string
}

const PARTNERS: Record<string, CommunityPartner> = {
  garvey: {
    slug: 'garvey',
    name: 'Garvey Senior Activity Center',
    shortName: 'Garvey Center',
    organization: "St. Mary's County Aging & Human Services",
    audience: 'For members and their families',
    heroFootnote: 'No account needed.',
    localHelpHeading: "Who to call in St. Mary's County.",
    localHelpIntro:
      'If something feels wrong, these are the people to call. The Garvey Center staff can help you make the first call.',
    logoUrl: '/partners/stmarys-seal.png',
    logoAlt: "St. Mary's County, Maryland seal",
    // Sampled from stmaryscountymd.gov (navy #0e1c2e, slate #4e6585, gold #ac912a).
    colors: { primary: '#0e1c2e', deep: '#08111d', accent: '#c5b26a' },
    address: ['23630 Hayden Farm Lane', 'Leonardtown, MD 20650'],
    phone: '(301) 475-4200 ext. 1080',
    tel: '+13014754200',
    hours: ['Monday to Thursday, 8 a.m. to 8 p.m.', 'Friday, 8 a.m. to 4:30 p.m.'],
    website: 'https://www.stmaryscountymd.gov/aging/garvey/',
    websiteLabel: 'stmaryscountymd.gov/aging',
    localHelp: [
      {
        name: "St. Mary's County Sheriff's Office",
        phone: '(301) 475-8008',
        tel: '+13014758008',
        description: 'Non-emergency line. Report a scam call, visit, or a loss to the Sheriff. In an emergency call 911.',
        url: 'https://www.firstsheriff.com/citizenreport/',
      },
      {
        name: 'Maryland Attorney General, Consumer Protection',
        phone: '1-888-743-0023',
        tel: '+18887430023',
        description: 'Free help and complaints for Maryland residents targeted by a scam or a dishonest business.',
        url: 'https://portal.oag.state.md.us/cpdportal/',
        linkLabel: 'File a complaint online',
      },
      {
        name: 'Senior Information & Assistance',
        phone: '(301) 475-4200 ext. 1050',
        tel: '+13014754200',
        description: "St. Mary's County Aging & Human Services. Benefits questions, Senior Medicare Patrol, and where to turn next.",
        url: 'https://www.stmaryscountymd.gov/aging/senior-information-overview/',
      },
    ],
    terminology: 'bank',
    accessCode: 'garvey2026',
  },
  // The Beekeeper 2 "Protect the Hive" campaign. Built 2026-09-10 for a call
  // with the film's head of promotions: a co-branded GranGuard face the
  // studio could point a national audience at. The first film's plot (a
  // retired teacher drained by a phishing call center) is the same story
  // GranGuard teaches people to spot, so the crossover writes itself.
  //
  // Everything here is placeholder branding: the honeycomb mark is our own
  // artwork, not studio key art, and the film is only named in text. Swap in
  // official assets once the studio provides them; keep the gate until the
  // studio has agreed to the co-brand.
  beekeeper: {
    slug: 'beekeeper',
    name: 'The Beekeeper 2',
    shortName: 'The Beekeeper 2',
    organization: 'Protect Your Hive campaign',
    partnerLabel: 'Presented with',
    audience: 'For you and everyone you look out for.',
    heroHeadline: 'Protect your hive.',
    heroAccent: '#f5b400',
    // No heroPattern: the honeycomb net fought the hero artwork (Lee, 2026-09-10).
    heroFootnote: 'No account needed.',
    localHelpHeading: 'Who to call if a scam gets through.',
    localHelpIntro:
      'If something feels wrong, these lines are free, national, and staffed by people who deal with this every day.',
    logoUrl: '/partners/beekeeper-mark.svg',
    logoAlt: 'Protect the Hive honeycomb mark',
    // Black and honey amber, the palette of the film's marketing.
    colors: { primary: '#111111', deep: '#000000', accent: '#f5b400' },
    localHelp: [
      {
        name: 'Federal Trade Commission',
        phone: '1-877-382-4357',
        tel: '+18773824357',
        description: 'Report a scam, a suspicious call, or a loss. Reports feed the cases that shut these operations down.',
        url: 'https://reportfraud.ftc.gov/',
      },
      {
        name: 'National Elder Fraud Hotline',
        phone: '1-833-372-8311',
        tel: '+18333728311',
        description: 'U.S. Department of Justice. Case managers help you report, recover what you can, and know what comes next.',
        url: 'https://www.justice.gov/elderjustice/national-elder-fraud-hotline',
      },
      {
        name: 'AARP Fraud Watch Network Helpline',
        phone: '1-877-908-3360',
        tel: '+18779083360',
        description: 'Free for everyone, member or not. Trained volunteers talk you through what happened and what to do.',
        url: 'https://www.aarp.org/money/scams-fraud/helpline/',
        linkLabel: 'About the helpline',
      },
    ],
    terminology: 'bank',
    accessCode: 'protectthehive',
  },
  // Edward A. Myerberg Center, Northwest Baltimore (CHAI / The Associated).
  // Built 2026-09-14 as a gated preview for Melanie Waxman, program director,
  // to re-open a warm lead with the sponsored-tenant offer. The mark is our
  // own placeholder (navy + amber sampled from myerberg.org: #2f4463,
  // #d9912f); swap in the center's logo once they agree to the co-brand.
  myerberg: {
    slug: 'myerberg',
    name: 'Edward A. Myerberg Center',
    shortName: 'Myerberg Center',
    organization: 'CHAI and The Associated',
    audience: 'For members and their families',
    heroFootnote: 'No account needed.',
    localHelpHeading: 'Who to call in Baltimore.',
    localHelpIntro:
      'If something feels wrong, these are the people to call. The Myerberg Center staff can help you make the first call.',
    logoUrl: '/partners/myerberg-mark.svg',
    logoAlt: 'Myerberg Center mark',
    colors: { primary: '#2f4463', deep: '#1f2f47', accent: '#d9912f' },
    address: ['3101 Fallstaff Road', 'Baltimore, MD 21209'],
    phone: '(410) 358-6856',
    tel: '+14103586856',
    hours: ['Monday to Thursday, 8 a.m. to 4 p.m.', 'Friday, 8 a.m. to noon'],
    website: 'https://www.myerberg.org/',
    websiteLabel: 'myerberg.org',
    localHelp: [
      {
        name: 'Baltimore Police, non-emergency',
        phone: '311',
        tel: '311',
        description: 'Report a scam call, visit, or a loss to the Baltimore Police Department. In an emergency call 911.',
        url: 'https://www.baltimorepolice.org/file-police-report',
      },
      {
        name: 'Maryland Attorney General, Consumer Protection',
        phone: '1-888-743-0023',
        tel: '+18887430023',
        description: 'Free help and complaints for Maryland residents targeted by a scam or a dishonest business.',
        url: 'https://portal.oag.state.md.us/cpdportal/',
        linkLabel: 'File a complaint online',
      },
      {
        name: 'Maryland Access Point, Baltimore City',
        phone: '(410) 396-2273',
        tel: '+14103962273',
        description: 'Baltimore City Division of Aging. Benefits questions, Senior Medicare Patrol, and where to turn next.',
        url: 'https://www.baltimorecity.gov/health/our-work/aging-services',
      },
    ],
    terminology: 'bank',
    accessCode: 'myerberg2026',
  },
  // Lafayette Federal Credit Union, Rockville MD. Built 2026-09-15 as a
  // gated demo for the 2026-09-22 meeting with Varun Gupta (SVP Business
  // Development) and Kate Purdy (PVP Marketing): the member-benefit layer of
  // the offer in docs/strategy/lafayette-offer.md. The mark is the emblem
  // from lfcu.org's own logo; palette sampled from lfcu.org (navy #1f4f79,
  // deep #123659, burgundy #7c2328). Keep the gate until they agree to the
  // co-brand.
  lafayette: {
    slug: 'lafayette',
    name: 'Lafayette Federal Credit Union',
    shortName: 'Lafayette Federal',
    organization: 'Serving Maryland, Washington, DC and Virginia since 1935',
    partnerLabel: 'A member benefit from',
    audience: 'For Lafayette members and their families',
    heroFootnote: 'No account needed.',
    localHelpHeading: 'Who to call if something feels wrong.',
    localHelpIntro:
      'If a call, text or email mentions your Lafayette account, hang up and call Lafayette on the number you already have. These are the other people who can help.',
    logoUrl: '/partners/lafayette-mark.svg',
    logoAlt: 'Lafayette Federal Credit Union mark',
    colors: { primary: '#1f4f79', deep: '#123659', accent: '#7c2328' },
    address: ['2701 Tower Oaks Boulevard', 'Rockville, MD 20852'],
    phone: '1-800-888-6560',
    tel: '+18008886560',
    website: 'https://www.lfcu.org/',
    websiteLabel: 'lfcu.org',
    localHelp: [
      // Numbers checked against lfcu.org/contact on 2026-09-24. The contact
      // page's #fraud section is Lafayette's card-fraud guidance; there is no
      // separate fraud-reporting page, so the link says what it is.
      {
        name: 'Lafayette Member Services',
        phone: '1-800-888-6560',
        tel: '+18008886560',
        extraPhones: [
          { label: 'Debit card fraud or a lost card', phone: '1-888-918-7802', tel: '+18889187802' },
          { label: 'Credit card fraud or a lost card', phone: '1-866-820-2904', tel: '+18668202904' },
        ],
        description:
          'Call Lafayette first about any suspicious contact that mentions your account, or a payment you are unsure about.',
        url: 'https://www.lfcu.org/contact/#fraud',
        linkLabel: 'Lafayette contact page',
      },
      {
        name: 'Federal Trade Commission',
        phone: '1-877-382-4357',
        tel: '+18773824357',
        description: 'Report a scam, a suspicious call, or a loss. Your report helps shut these operations down.',
        url: 'https://reportfraud.ftc.gov/',
        linkLabel: 'Report at ReportFraud.ftc.gov',
      },
      {
        name: 'FBI Internet Crime Complaint Center (IC3)',
        description: 'Report online fraud, especially if money was sent by wire, crypto, or gift card. Online reports only.',
        url: 'https://www.ic3.gov/',
        linkLabel: 'Report at ic3.gov',
      },
      {
        name: 'Maryland Attorney General, Consumer Protection',
        phone: '1-888-743-0023',
        tel: '+18887430023',
        description: 'Free help and complaints for Maryland residents targeted by a scam or a dishonest business.',
        url: 'https://portal.oag.state.md.us/cpdportal/',
        linkLabel: 'File a complaint online',
      },
      {
        name: 'AARP Fraud Watch Network Helpline',
        phone: '1-877-908-3360',
        tel: '+18779083360',
        description: 'Free for everyone, member or not, in Maryland, DC and Virginia. Trained volunteers talk you through what happened and what to do.',
        url: 'https://www.aarp.org/money/scams-fraud/helpline/',
        linkLabel: 'About the helpline',
      },
    ],
    // Matches lfcu.org/contact (checked 2026-09-24).
    verifyContact: {
      heading: 'Check with Lafayette',
      body:
        'If this mentions your Lafayette account, card or a payment, don’t use any number or link in the message. Call Lafayette on a number you already trust, like the one on the back of your card.',
      phones: [
        { label: 'Member Services', phone: '1-800-888-6560', tel: '+18008886560' },
        { label: 'Debit card fraud or a lost card', phone: '1-888-918-7802', tel: '+18889187802' },
        { label: 'Credit card fraud or a lost card', phone: '1-866-820-2904', tel: '+18668202904' },
      ],
      website: 'https://www.lfcu.org/',
      websiteLabel: 'lfcu.org',
      firstCall: {
        lead: 'Lafayette member? Call Lafayette first on ',
        phone: '1-800-888-6560',
        tel: '+18008886560',
        after: ', or the number on the back of your card.',
      },
    },
    terminology: 'creditUnion',
    accessCode: 'lafayette2026',
  },
  // Howard County, MD: gated demo for the Office on Aging and Independence and
  // the Office of Consumer Protection (Scam Squad), built 2026-09-16 for the
  // Maryland county push (docs/strategy/maryland-outbound-playbook.md). The
  // mark is our own placeholder in the county's navy and green; swap in the
  // county seal once they agree to a county-branded programme.
  howard: {
    slug: 'howard',
    name: 'Howard County Office on Aging and Independence',
    shortName: 'Howard County',
    organization: 'Howard County, Maryland',
    partnerLabel: 'Brought to you by',
    audience: 'For Howard County residents and their families',
    heroFootnote: 'No account needed.',
    localHelpHeading: 'Who to call in Howard County.',
    localHelpIntro:
      'If something feels wrong, these are the people to call. Staff at any 50+ Center can help you make the first call.',
    logoUrl: '/partners/howard-mark.svg',
    logoAlt: 'Howard County mark',
    colors: { primary: '#1f3b57', deep: '#142a40', accent: '#acd03a' },
    address: ['9830 Patuxent Woods Drive', 'Columbia, MD 21046'],
    phone: '(410) 313-1234',
    tel: '+14103131234',
    website: 'https://www.howardcountymd.gov/aging-independence',
    websiteLabel: 'howardcountymd.gov/aging-independence',
    localHelp: [
      {
        name: 'Howard County Police, non-emergency',
        phone: '(410) 313-2200',
        tel: '+14103132200',
        description: 'Report a scam call, visit, or a loss to the Howard County Police. In an emergency call 911.',
        url: 'https://www.howardcountymd.gov/police/protect-yourself-frauds-and-scams',
      },
      {
        name: 'Howard County Office of Consumer Protection',
        phone: '(410) 313-6420',
        tel: '+14103136420',
        description: 'The county office for scams and dishonest businesses. Runs the Scam Squad and free seminars.',
        url: 'https://www.howardcountymd.gov/consumer-protection',
      },
      {
        name: 'Maryland Attorney General, Consumer Protection',
        phone: '1-888-743-0023',
        tel: '+18887430023',
        description: 'Free help and complaints for Maryland residents targeted by a scam or a dishonest business.',
        url: 'https://portal.oag.state.md.us/cpdportal/',
        linkLabel: 'File a complaint online',
      },
    ],
    terminology: 'bank',
    accessCode: 'howard2026',
  },
  // Montgomery County, MD: gated demo for County Recreation (runs the eight
  // senior centers) and DHHS Aging & Disability Services, built 2026-09-16.
  // Lafayette FCU's home county; five of the six centers in the Lafayette
  // offer are here. Placeholder mark; swap in the county seal on agreement.
  montgomery: {
    slug: 'montgomery',
    name: 'Montgomery County Senior Centers',
    shortName: 'Montgomery County',
    organization: 'Montgomery County Recreation and Aging & Disability Services',
    partnerLabel: 'Brought to you by',
    audience: 'For Montgomery County residents and their families',
    heroFootnote: 'No account needed.',
    localHelpHeading: 'Who to call in Montgomery County.',
    localHelpIntro:
      'If something feels wrong, these are the people to call. Staff at any county senior center can help you make the first call.',
    logoUrl: '/partners/montgomery-mark.svg',
    logoAlt: 'Montgomery County mark',
    colors: { primary: '#1d3f73', deep: '#132b50', accent: '#f2b632' },
    address: ['401 Hungerford Drive', 'Rockville, MD 20850'],
    phone: '(240) 777-3000',
    tel: '+12407773000',
    website: 'https://www.montgomerycountymd.gov/rec/facilities/seniorcenters/',
    websiteLabel: 'montgomerycountymd.gov/rec',
    localHelp: [
      {
        name: 'Montgomery County Police, non-emergency',
        phone: '(301) 279-8000',
        tel: '+13012798000',
        description: 'Report a scam call, visit, or a loss to the Montgomery County Police. In an emergency call 911.',
        url: 'https://www.montgomerycountymd.gov/montgomery-county-police-department/how-do-i/how-make-police-report',
      },
      {
        name: 'Montgomery County Office of Consumer Protection',
        phone: '(240) 777-3636',
        tel: '+12407773636',
        description: 'The county office for scams and dishonest businesses. Sends speakers to senior centers on request.',
        url: 'https://www.montgomerycountymd.gov/office-consumer-protection/file-consumer-complaint',
      },
      {
        name: 'Aging & Disability Resource Unit / Adult Protective Services',
        phone: '(240) 777-3000',
        tel: '+12407773000',
        description: 'Montgomery County DHHS. Benefits questions, Adult Protective Services, and where to turn next.',
        url: 'https://www.montgomerycountymd.gov/seniors/finance-consumer-safety',
      },
    ],
    terminology: 'bank',
    accessCode: 'montgomery2026',
  },
}

const OVERRIDE_KEY = 'sd_partner'

export function isCommunityPartnerSlug(slug: string | null | undefined): boolean {
  return !!slug && slug in PARTNERS
}

/** Look a partner up by slug. Safe on the server (edge middleware uses it). */
export function getCommunityPartner(slug: string | null | undefined): CommunityPartner | null {
  if (!slug) return null
  return PARTNERS[slug.toLowerCase()] ?? null
}

export function resolveCommunityPartner(): CommunityPartner | null {
  if (typeof window === 'undefined') return null
  const params = new URLSearchParams(window.location.search)
  const param = params.get('partner')?.toLowerCase()
  if (param !== null && param !== undefined) {
    try {
      if (param in PARTNERS) window.sessionStorage.setItem(OVERRIDE_KEY, param)
      else window.sessionStorage.removeItem(OVERRIDE_KEY)
    } catch {
      /* ignore */
    }
    return PARTNERS[param] ?? null
  }
  try {
    const stored = window.sessionStorage.getItem(OVERRIDE_KEY)
    if (stored && stored in PARTNERS) return PARTNERS[stored]
  } catch {
    /* ignore */
  }
  const sub = window.location.hostname.toLowerCase().split('.')[0]
  return PARTNERS[sub] ?? null
}

/** Resolved once per page load; the partner never changes within a page. */
export const COMMUNITY_PARTNER: CommunityPartner | null = resolveCommunityPartner()
